import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import VideoTravel from '../../assets/video/sytCompra.mp4';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
import { useTranslation } from 'react-i18next';

// Componente per l'indicatore "sta scrivendo" con tre pallini animati
const TypingIndicator = () => {
  return (
    <div className="flex items-center space-x-1">
      <span className="dot" style={{ animation: "bounce 1.4s infinite", animationDelay: "0s" }}></span>
      <span className="dot" style={{ animation: "bounce 1.4s infinite", animationDelay: "0.2s" }}></span>
      <span className="dot" style={{ animation: "bounce 1.4s infinite", animationDelay: "0.4s" }}></span>
    </div>
  );
};

const AdvertisingBuy = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const { t } = useTranslation();

  // Frasi da animare (assicurarsi che advertising.phrases2 sia un array di stringhe)
  const phrases = t('advertising.phrases2', { returnObjects: true });

  // Stati per la scrittura a fasi:
  // waitingBefore: mostra i pallini (nessun testo) e poi inizia a scrivere il primo carattere
  // typing: il messaggio viene scritto (i pallini non vengono mostrati)
  // waitingAfter: il messaggio completo resta visibile per un attimo
  // deleting: cancellazione del messaggio (elimina carattere per carattere, poi si riparte con waitingBefore)
  const [animationStart, setAnimationStart] = useState(false);
  const [phase, setPhase] = useState("waitingBefore");
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");

  // Avvio dell'animazione dopo 2 secondi
  useEffect(() => {
    const timeout = setTimeout(() => setAnimationStart(true), 2000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!animationStart) return;
    const currentPhrase = phrases[currentPhraseIndex];
    let timeout;

    switch (phase) {
      case "waitingBefore":
        // Mostra i pallini per 1500ms, poi inizia a scrivere il primo carattere
        timeout = setTimeout(() => {
          setDisplayedText(currentPhrase.substring(0, 1));
          setPhase("typing");
        }, 1500);
        break;
      case "typing":
        if (displayedText.length < currentPhrase.length) {
          let typingSpeed = 100 + Math.floor(Math.random() * 50);
          timeout = setTimeout(() => {
            setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
          }, typingSpeed);
        } else {
          // Messaggio completo: resta visibile per un attimo
          timeout = setTimeout(() => {
            setPhase("waitingAfter");
          }, 500);
        }
        break;
      case "waitingAfter":
        // Il messaggio completo resta visibile per un breve istante prima di iniziare la cancellazione
        timeout = setTimeout(() => {
          setPhase("deleting");
        }, 500);
        break;
      case "deleting":
        if (displayedText.length > 1) {
          let deletingSpeed = 50 + Math.floor(Math.random() * 50);
          timeout = setTimeout(() => {
            setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
          }, deletingSpeed);
        } else if (displayedText.length === 1) {
          // Quando resta un solo carattere, passa alla frase successiva e torna in waitingBefore
          timeout = setTimeout(() => {
            const nextIndex = (currentPhraseIndex + 1) % phrases.length;
            setCurrentPhraseIndex(nextIndex);
            setDisplayedText("");
            setPhase("waitingBefore");
          }, 500);
        }
        break;
      default:
        break;
    }

    return () => clearTimeout(timeout);
  }, [animationStart, phase, displayedText, currentPhraseIndex, phrases]);

  // Gestione della navigazione
  const handleButtonClick = () => {
    if (isAuthenticated) {
      navigate('/experience');
    } else {
      navigate('/register');
    }
  };

  const handleButtonFaq = () => {
    // Naviga verso la guida generale
    navigate('/general-guide');
  };

  return (
    <div className="flex flex-wrap justify-between items-start mb-4 border-b border-gray-300 pb-14 mt-12 md:mt-32">
      {/* Stili per l'animazione dei pallini (bounce) e per il cursore */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .dot {
          width: 8px;
          height: 8px;
          background-color: currentColor;
          border-radius: 50%;
          display: inline-block;
        }
        .animate-blink {
          animation: blink 1s steps(2, start) infinite;
        }
        @keyframes blink {
          to {
            visibility: hidden;
          }
        }
      `}</style>
      
      {/* VIDEO */}
      <div className="w-full md:w-1/2 lg:w-2/5 flex justify-center items-center order-1 md:order-1 rounded-3xl">
        <video
          src={VideoTravel}
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline="true"
          className="w-[350px] h-[350px] lg:w-[600px] lg:h-[500px] md:h-[500px] md:w-[500px] object-cover rounded-xl"
        />
      </div>

      {/* TESTO */}
      <div className="relative w-full md:w-1/2 flex flex-col order-2 md:order-2 min-h-[350px] md:mt-0 md:min-h-[500px] lg:min-h-[500px]">
        <h1
          className="text-4xl lg:text-6xl md:text-5xl font-bold font-italic uppercase md:mb-12 mb-4 mt-12 md:mt-0 "
          style={{ lineHeight: '1.2' }}
        >
          {t('advertising.title2')} <br />  {t('advertising.title3')}{" "}
          <span className="italic font-bold text-green-adventure">
            save your travel!
          </span>
        </h1>

        {/* AREA CHAT */}
        <div className="flex-grow space-y-4">
          {/* Bubble Fisso (domanda fissa, allineata a sinistra) */}
          <div className="flex justify-start">
            <div 
              className="
                text-burnt-coffee
                mb-4
                rounded-tl-full
                text-xl md:text-3xl
                ml-1
                flex items-center
                space-x-2
                p-4
                rounded-br-full
                rounded-tr-full
                rounded-tl-full
                bg-gray-300
                min-h-[3rem] 
                whitespace-nowrap
              "
            >
              <span>{t('advertising.title5')}</span>
            </div>
          </div>
          {/* Bubble Animato (risposta animata, allineata a destra) */}
          <motion.div
            className="flex justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <div 
              className="
                text-burnt-coffee
                mb-12
                rounded-tl-full
                text-xl md:text-3xl
                ml-1
                flex items-center
                space-x-2
                border border-claudy-blue
                p-4
                rounded-bl-full
                rounded-tr-full
                rounded-tl-full
                bg-claudy-blue
                h-[4rem]
                whitespace-nowrap
              "
            >
              {phase === "waitingBefore" ? (
                <TypingIndicator />
              ) : (
                <span>{displayedText}</span>
              )}
              <span className="ml-1 animate-blink">|</span>
            </div>
          </motion.div>
        </div>

        {/* BOTTONI */}
        <div className="w-full flex flex-col md:flex-row md:justify-between">
          <button
            onClick={handleButtonFaq}
            className="py-2 md:pl-1 font-heading w-full md:w-2/3 uppercase text-wild-ocean underline font-bold text-lg md:text-xl hover:text-green-adventure text-center md:text-start"
          >
            {t('general.howFunction')}
          </button>
          <button
            onClick={handleButtonClick}
            className="py-2 mt-4 md:mt-0 md:mr-6 bg-wild-ocean font-heading w-full md:w-1/3 uppercase text-white rounded-lg text-lg md:text-lg hover:bg-green-adventure text-center"
          >
            {t('general.startBuy')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvertisingBuy;
